import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useClinics } from '../../hooks/useClinics';
import permissionsService from '../../services/appointments/permissions';

/**
 * Sentinel option id for "Unassigned (no clinic)". Real Core clinic ids are
 * positive, so a negative id never collides with one.
 */
const UNASSIGNED_OPTION_ID = -1;

interface ClinicOption {
  id: number;
  name: string;
}

interface Props {
  selectedClinicIds: number[];
  includeUnassigned: boolean;
  onChange: (next: { clinicIds: number[]; includeUnassigned: boolean }) => void;
}

/**
 * Clinic multiselect for the Comms Center lists. Options come from
 * `CommonService.getClinics()`; the server still enforces the real boundary
 * (clamps to the caller's permitted clinics), so this is convenience narrowing.
 *
 * An "Unassigned (no clinic)" option appears only when the caller holds the
 * `unassigned_comms:read` permission (surfaced via `GET /permissions` as
 * `canSeeUnassigned`). Selecting only "Unassigned" returns NULL-clinic records
 * alone; selecting clinics together with "Unassigned" ORs the two.
 */
export const ClinicFilter = ({ selectedClinicIds, includeUnassigned, onChange }: Props) => {
  const { clinics } = useClinics({ notifyOnError: false });
  const [canSeeUnassigned, setCanSeeUnassigned] = useState<boolean>(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const response = await permissionsService.fetchPermissions();
      if (active) {
        setCanSeeUnassigned(!!response.data?.canSeeUnassigned);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const options: ClinicOption[] = [
    ...clinics.map((clinic) => ({ id: clinic.id, name: clinic.name })),
    ...(canSeeUnassigned ? [{ id: UNASSIGNED_OPTION_ID, name: 'Unassigned (no clinic)' }] : []),
  ];

  const value = options.filter(
    (option) =>
      selectedClinicIds.includes(option.id) ||
      (option.id === UNASSIGNED_OPTION_ID && includeUnassigned),
  );

  return (
    <Box display='flex' flexDirection='column' alignItems='flex-start' sx={{ minWidth: '220px' }}>
      <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Clinic</Typography>
      <Autocomplete
        multiple
        fullWidth
        size='small'
        disableCloseOnSelect
        options={options}
        value={value}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onChange={(_event, selected) => {
          onChange({
            clinicIds: selected
              .filter((option) => option.id !== UNASSIGNED_OPTION_ID)
              .map((option) => option.id),
            includeUnassigned: selected.some((option) => option.id === UNASSIGNED_OPTION_ID),
          });
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder='All my clinics' sx={{ minWidth: '220px' }} />
        )}
      />
    </Box>
  );
};
